import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button, SegmentedControl, Sheet } from '@ai-tutor/ui';
import type { ProgressPeriod } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { PERIOD_OPTIONS } from './PeriodToggle';

interface ProgressPhoneBarProps {
  period: ProgressPeriod;
  onChange: (period: ProgressPeriod) => void;
  pastYear: number | undefined;
  onPastYear: (year: number | undefined) => void;
  /** Newest first. Empty hides "Past years" (no archive to offer). */
  years: number[];
}

/**
 * The phone's period bar: This week / This month / This year, plus "Past
 * years", which opens a sheet of the archive years.
 *
 * Drawn at the toggle's own size (about 38px tall), with each control's
 * 44px tap area added invisibly by `tap-target` - growing the segments to
 * 44px made the bar a 50px slab.
 *
 * It replaces two things the desktop header has: the toggle, and the
 * "History: 2025 2024" chips under it. On a phone those chips were 22px
 * targets on their own row, and the header they sat in was pinned, so title,
 * toggle and chips took a third of the screen for the whole visit. Here the
 * title scrolls away and only this bar stays (`ProgressLayout` makes it
 * sticky), since it is the one control that changes everything below it.
 *
 * Inside an archive year the bar says so and offers the way back, as the
 * desktop toggle's "Viewing 2025 · ← Current" does.
 */
export function ProgressPhoneBar({
  period,
  onChange,
  pastYear,
  onPastYear,
  years,
}: ProgressPhoneBarProps) {
  const [open, setOpen] = useState(false);

  if (pastYear !== undefined) {
    return (
      <div className='flex items-center justify-between gap-3 rounded-lg border border-border bg-card py-1 pl-4 pr-1'>
        <span className='text-sm font-medium text-foreground'>
          Viewing {pastYear}
        </span>
        <Button
          variant='ghost'
          size='sm'
          className='tap-target h-7'
          onClick={() => onPastYear(undefined)}
        >
          Back to this year
        </Button>
      </div>
    );
  }

  return (
    <div className='flex items-stretch gap-2'>
      <SegmentedControl
        label='Time period'
        options={PERIOD_OPTIONS}
        value={period}
        onChange={onChange}
        size='md'
        className='min-w-0 flex-1 bg-card [&>button]:flex-1 [&>button]:px-1'
        segmentClassName='tap-target'
      />
      {years.length > 0 && (
        <button
          type='button'
          aria-haspopup='dialog'
          aria-expanded={open}
          onClick={() => setOpen(true)}
          className={cn(
            'tap-target inline-flex shrink-0 items-center gap-1 rounded-lg border border-border bg-card px-3 text-xs font-medium text-muted-foreground',
            'hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
          )}
        >
          Past years
          <ChevronDown className='h-3.5 w-3.5' />
        </button>
      )}
      <Sheet
        isOpen={open}
        onClose={() => setOpen(false)}
        title='Past years'
        size='auto'
      >
        <p className='px-4 pb-2 pt-3 text-sm text-muted-foreground'>
          A whole year at a time, week by week.
        </p>
        <ul className='px-2 pb-[max(1rem,env(safe-area-inset-bottom))]'>
          {years.map(year => (
            <li key={year}>
              <button
                type='button'
                onClick={() => {
                  setOpen(false);
                  onPastYear(year);
                }}
                className='flex min-h-11 w-full items-center rounded-lg px-3 text-left text-base font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
              >
                {year}
              </button>
            </li>
          ))}
        </ul>
      </Sheet>
    </div>
  );
}
